import { ObjectStateManager } from "../../../api/robocore/BaseDataChannel";
import { CameraChannel } from "../../../api/robocore/CameraChannel";
import { ControlChannel } from "../../../api/robocore/ControlChannel";
import { CostmapChannel } from "../../../api/robocore/CostmapChannel";
import { BehaviorDescriptor, NavMode, RobotPostureMode, RobotRecordingStatus, RobotTwist, VehicleStatus } from "../../../api/robocore/dex_interfaces";
import { GpsChannel } from "../../../api/robocore/GPSChannel";
import { IMUChannel } from "../../../api/robocore/IMUChannel";
import { LidarChannel } from "../../../api/robocore/LidarChannel";
import { MapChannel } from "../../../api/robocore/MapChannel";
import { ObjectDetectionChannel } from "../../../api/robocore/ObjectDetectionChannel";
import { OdomChannel } from "../../../api/robocore/OdomChannel";
import { RobocoreClient } from "../../../api/robocore/RobocoreClient";
import { UIService } from "../../../api/UIService";
import { getCreds } from "../../../contexts/PageContext";
import { createManifest, RobotManifest } from "../../../models/Robot";
import { RobotVisual } from "../../../visuals/RobotVisual";
import { BaseAgent } from "../agents/BaseAgent";
import * as THREE from 'three'
import { MapBuilder } from "./MapBuilder";
import { RTVisualizer } from "./visualizers/RTVisualizer";
import { Vec3 } from "../../../models/SyntheticMap";
import { Lidar3DVisualizer } from "./visualizers/Lidar3DVisualizer";
import { RobotMap } from "./RobotMap";
import { RobotSlamMap } from "../../../models/Deployment";
import { CostmapVisualizer } from "./visualizers/CostmapVisualizer";
import { VisualEvents } from "../../../visuals/BaseVisual";
import { RobotViewManager } from "./RobotViewManager";
import { WREvents } from "../../WREvents";
import { SLAMMapVisualizer } from "./visualizers/SLAMMapVisualizer";
import { SynthMapChannel } from "../../../api/robocore/SynthMapChannel";
import { SynthMapVisualizer } from "./visualizers/SynthMapVisualizer";
import { MeshChannel } from "../../../api/robocore/MeshChannel";
import { MeshVisualizer } from "./visualizers/MeshVisualizer";

export enum RobotConnectionState {
  Connecting = 'connecting',
  Connected = 'connected',
  Disconnected = 'disconnected'
}

const VSLAM_TEST_MODE = true;

export class RobotAgent extends BaseAgent implements ObjectStateManager, VehicleStatus {
  public robotVisual: RobotVisual;
  public navMode = NavMode.Unknown;
  public odom: number[] = [];
  public recordingStatus = RobotRecordingStatus.Off;
  public behaviorStep?: string | undefined;
  public initialPose?: number[] | undefined = [];
  public mapRequest?: string | undefined = '';
  public availableCameras: string[] = [];
  public selectedCamera: string = '';
  gps: Vec3 = { x: 0, y: 0, z: 0 };

  public behavior: BehaviorDescriptor = {
    name: 'idle',
    params: {}
  }
  public mapBuilder: MapBuilder;
  public connection = new RobocoreClient(this);
  public message = '';
  public postureMode = RobotPostureMode.Unknown;
  public manifest: RobotManifest = createManifest();
  public suspendLocationUpdates = false;
  public connectionState = RobotConnectionState.Disconnected;
  public robotId: string;
  public mapChannel: MapChannel;
  public controlChannel: ControlChannel;
  public objectDetectionChannel = new ObjectDetectionChannel();
  public synthMapChannel = new SynthMapChannel();
  public visualizers: RTVisualizer[] = [];
  public meshChannel = new MeshChannel();
  // public lidar2dVisualizer = new Lidar2DVisualizer(this);
  public lidar3dVisualizer = new Lidar3DVisualizer(this);
  public costmapVisualizer = new CostmapVisualizer(this);
  public slamMapVisualizer = new SLAMMapVisualizer(this);
  public synthMapVisualizer = new SynthMapVisualizer(this);
  public meshVisualizer = new MeshVisualizer(this);

  public lidarChannel: LidarChannel;
  public odomChannel: OdomChannel;
  public lidar2dData: number[] = [];
  public lidar3dData: number[] = [];
  public costmapData: RobotSlamMap | null = null;
  public costmapChannel: CostmapChannel;
  public viewManager: RobotViewManager;
  public poi: any = {};
  constructor(visual: RobotVisual, public live = false) {
    super(visual);
    this.viewManager = new RobotViewManager(this);
    this.robotVisual = visual;
    this.robotId = visual.robotId;
    this.connection.onConnectionStateChanged = (state) => {
      this.updateRobot({ connectionState: state as RobotConnectionState });
      return false;
    };
    this.mapChannel = new MapChannel();
    this.mapChannel.on('map', async (data: RobotSlamMap) => {
      await this.slamMapVisualizer.updateSlamMap(data);
    });
    this.controlChannel = new ControlChannel();
    this.lidarChannel = new LidarChannel();
    this.odomChannel = new OdomChannel();
    this.costmapChannel = new CostmapChannel();
    this.costmapChannel.on('map', (data: RobotSlamMap) => {
      this.updateRobot({ costmapData: data });
    });
    this.connection.channels = [
      this.controlChannel,
      this.mapChannel,
      this.costmapChannel,
      this.lidarChannel,
      this.objectDetectionChannel,
      this.synthMapChannel,
      this.meshChannel,
    ];
    this.mapBuilder = new MapBuilder(this.visual.wr.dataService.activeSite!);
    if (!this.live) {
      this.connection.channels = [
        ...this.connection.channels,
        this.odomChannel,
        new CameraChannel(),
        new ObjectDetectionChannel(),
        new GpsChannel(),
        new IMUChannel(),
      ];
      this.registerPhysXHandler('lidar2d', (data: number[]) => {
        // this.lidar2dVisualizer.update(data);
        this.lidar2dData = data;
        if (this.connectionState === RobotConnectionState.Connected) {
          this.lidarChannel.send(data);
        }
      });
      this.registerPhysXHandler('lidar3d', (data: number[]) => {
        this.lidar3dData = data;
        this.lidar3dVisualizer.update(data);
      });
    }
    this.robotVisual.on(VisualEvents.DataUpdated, () => {
      this.lidar3dVisualizer.setActive(this.robotVisual.visualizeSensors);
      this.costmapVisualizer.setActive(this.robotVisual.visualizeSensors);
      this.slamMapVisualizer.setActive(this.robotVisual.visualizeSensors);
      this.meshVisualizer.setActive(this.robotVisual.visualizeSensors);
    });
    this.lidar3dVisualizer.setActive(this.robotVisual.visualizeSensors);
    this.costmapVisualizer.setActive(this.robotVisual.visualizeSensors);
    this.meshVisualizer.setActive(this.robotVisual.visualizeSensors);
    this.slamMapVisualizer.setActive(this.robotVisual.visualizeSensors);
    this.addState('Show Camera', async () => {
      this.visual.wr.emitEvent(WREvents.CameraSelected, null);
      this.visual.wr.emitEvent(WREvents.CameraSelected, this.visual, 0);
      return true;
    }, async () => {
      // Disable FPV camera on robot
      this.visual.wr.emitEvent(WREvents.CameraSelected, null);
    });

    this.meshChannel.on('data', (data: ArrayBuffer) => {
      this.meshVisualizer.updateMesh(data);
    });

    // this.on(AgentEvents.StateChange, (dgram) => {
    //   if (dgram.poi) {
    //     if (dgram.poi.x !== undefined) {

    //     }
    //   }
    // });
  }

  public updateRobot(dgram: Partial<RobotAgent>) {
    this.updateState(dgram);
  }


  public updateState(dgram: Partial<BaseAgent>): void {
    super.updateState(dgram);
    if (dgram.robotTwist) {
      const [lx, ly, lz] = dgram.robotTwist.linear;
      const [ax, ay, az] = dgram.robotTwist.angular;
      const linear = new THREE.Vector3(lx, ly, lz);
      // linear.applyEuler(this.robotVisual.obj.rotation);
      // this.robotVisual.velocity.linear = linear;
      // this.robotVisual.velocity.angular.set(0, 0, az);
      // this.robotVisual.animator.walk(dgram.robotTwist);
    } else if ((dgram as any).postureMode) {
      const postureMode = (dgram as any).postureMode as RobotPostureMode;
      if (postureMode === RobotPostureMode.StandDown) {
        this.robotVisual.animator.layDown();
      } else if (postureMode === RobotPostureMode.WalkReady) {
        this.robotVisual.animator.standUp();
      }
    } else if (dgram.euler) {
      // console.log(`Updating robot euler: ${dgram.euler}`);
      const [ex, ey, ez] = dgram.euler;
      this.robotVisual.obj.children[0].rotation.set(ex, ey, ez);
    }
    const robotDgram = dgram as Partial<RobotAgent>;
    if (robotDgram.odom && VSLAM_TEST_MODE) {
      // console.log('Updating robot odom: ', robotDgram.odom);
      const [x, y, z, rz] = robotDgram.odom;
      this.robotVisual.obj.position.set(x, y, z);
      this.robotVisual.obj.rotation.set(0, 0, rz);
    }
  }

  getChildStateManager(name: string): ObjectStateManager | null {
    return null;
  }

  getUIService(): UIService {
    // THIS IS INTENTIONAL FOR NOW
    throw new Error("Method not implemented.");
  }

  getActorId(): string {
    return this.visual._id;
  }

  public async switchNavMode(mode: NavMode) {
    let map: RobotMap;
    if (mode === NavMode.Auto) {
      map = await this.mapBuilder.build(true);
      await this.mapBuilder.previewMap(map, true, 2000);
    }
    if (this.connectionState !== RobotConnectionState.Connected) {
      console.warn("Robot is not connected");
      return;
    }
    if (mode === NavMode.Auto) {
      this.mapChannel.sendBytes(map!.data!);
    }
    this.updateRobot({ navMode: NavMode.Unknown });
    this.controlChannel.sendRequest({ navMode: mode });
  }


  public isDrone() { return false; }

  public async setup() {
    const { user } = getCreds();

  }

  public async connect() {
    // this.updateRobot({ connectionState: RobotConnectionState.Connecting });
    const { user } = getCreds();
    await this.connection.connect(this.robotId, user._id);
  }

  public async disconnect() {
    await this.connection.disconnect();
  }

  public async loop() {
    if (this.connectionState !== RobotConnectionState.Connected) {
      return;
    }
    const pos = this.visual.obj.position;
    const rot = this.visual.obj.rotation;
    if (VSLAM_TEST_MODE) {

    } else {
      this.odomChannel.send([pos.x, pos.y, 0, rot.z]);
      this.lidarChannel.send(this.lidar2dData);
    }
  }

  public async deactivate() {
    const { user } = getCreds();
    await this.connection.disconnect();
  }

  public setNavGoal(position: Vec3) {
    this.controlChannel.sendRequest({ navGoal: [position.x, position.y, 0] });
  }

  public createNavGoal() {
    this.visual.wr.userEventer.positionSelectAction = (position) => {
      if (this.connectionState !== RobotConnectionState.Connected) {
        console.warn("Robot is not connected");
        return;
      }
      this.setNavGoal(position);
    };
  }

  updateTwist(robotTwist: RobotTwist) {
    if (this.live && this.connectionState === RobotConnectionState.Connected) {
      this.controlChannel.sendRequest({ robotTwist });
    } else {
      this.updateRobot({ robotTwist });
    }
  }

  public requestRecording(status: RobotRecordingStatus | string) {
    this.controlChannel.sendRequest({ recordingStatus: status });
  }

  public requestPostureMode(postureMode: RobotPostureMode) {
    this.controlChannel.sendRequest({ postureMode });
  }

  public render(delta: number): boolean {
    const res = super.render(delta);
    this.viewManager.render(delta);
    return res;
  }
}